import { isReleaseThemeId, type ReleaseThemeId } from '../releaseThemes';

import { STORY_FORMATS, storyFormatOf, type StoryFormatId } from './storyFormats';

/**
 * The cover sizes of a theme frame in one format (the outer box of the frame), the space above the
 * large cover of each moment, and the room under it.
 */
export interface StoryCoverSizes {
  preSave: number;
  outNow: number;
  /** The cover of an announce feed post (a Story has no announce layout: its countdown uses `small`). */
  announce: number;
  small: number;
  marginTop: { preSave: number; outNow: number; announce: number };
  /**
   * The room under a large cover and beside the small one: the hard shadow of the Hype sticker of
   * a feed post (design e: "20 px margin under it (lyric: right of it)"). 0 for the other themes.
   */
  marginBottom: number;
}

/**
 * The Story frame of each release theme (design `asset-redesign/b Story - new`, the table "Theme
 * frame table" and the CSS of `StoryFrame.dc.html`). The theme frame is the same for every moment;
 * the moment changes only the hero and the call to action. Every size is in the 1080 x 1920 space
 * of the render.
 */
export interface StoryFrame {
  /** The side margin of the safe column. Dreamy keeps 108 px (10 %), every other theme 84 px (7.8 %). */
  marginX: number;
  /**
   * The size factors of the theme (`k`, `n`, `l` of the design and the factor of the text font):
   * a narrow or a wide font keeps the same line width.
   */
  factors: { display: number; number: number; lyric: number; text: number };
  /** The colour of the hero text: the accent, or the text colour (Noir, Retro). */
  hero: 'accent' | 'text';
  /** The pill: accent on onAccent, surface with accent text (Hype), text on the background (Noir), or an accent tube (Neon). */
  pill: 'accent' | 'surface' | 'text' | 'tube';
  /** The kicker: plain text, a black label (Hype) or an accent tube box (Neon). */
  kicker: 'plain' | 'label' | 'tube';
  /** The ornament between the hero word and the title of an out-now Story. Null: none. */
  ornament: 'hairline' | 'bar' | 'line' | null;
  /** The letter spacing of the lyric (em). Null: the letter spacing of the display role. */
  lyricTracking: number | null;
  /** The cover sizes of a Story (the outer box of the frame) and the space above the large cover of each moment. */
  cover: StoryCoverSizes;
  /** The cover sizes of a feed post (design `e Feed post - new`, the build spec per moment). */
  feedCover: StoryCoverSizes;
  /** The pill text size in px. Null: 44 px times the text factor. Handmade writes 54 px with the marker font. */
  pillTextPx: number | null;
}

const BASE: StoryFrame = {
  marginX: 84,
  factors: { display: 1, number: 1, lyric: 1, text: 1 },
  hero: 'accent',
  pill: 'accent',
  kicker: 'plain',
  ornament: null,
  lyricTracking: null,
  cover: {
    preSave: 460,
    outNow: 580,
    announce: 460,
    small: 200,
    marginTop: { preSave: 0, outNow: 0, announce: 0 },
    marginBottom: 0,
  },
  feedCover: {
    preSave: 420,
    outNow: 500,
    announce: 360,
    small: 180,
    marginTop: { preSave: 0, outNow: 0, announce: 0 },
    marginBottom: 0,
  },
  pillTextPx: null,
};

export const STORY_FRAMES: Readonly<Record<ReleaseThemeId, StoryFrame>> = {
  clean: { ...BASE, ornament: 'hairline' },
  hype: {
    ...BASE,
    factors: { display: 1.3, number: 1.15, lyric: 0.85, text: 1 },
    pill: 'surface',
    kicker: 'label',
    ornament: 'bar',
    // The tilted sticker needs room above it.
    cover: { ...BASE.cover, outNow: 540, marginTop: { preSave: 0, outNow: 24, announce: 0 } },
    // A feed post: room above the tilted sticker and under its 20 px hard shadow.
    feedCover: {
      ...BASE.feedCover,
      outNow: 470,
      marginTop: { preSave: 16, outNow: 16, announce: 0 },
      marginBottom: 20,
    },
  },
  dreamy: { ...BASE, marginX: 108, factors: { display: 1.25, number: 1.1, lyric: 1, text: 0.9 } },
  dark: {
    ...BASE,
    factors: { display: 0.72, number: 0.95, lyric: 1.2, text: 0.82 },
    hero: 'text',
    pill: 'text',
    ornament: 'line',
    lyricTracking: 0.04,
  },
  warm: { ...BASE, factors: { display: 0.95, number: 1, lyric: 1, text: 1.05 } },
  retro: { ...BASE, factors: { display: 0.9, number: 0.9, lyric: 0.95, text: 0.9 }, hero: 'text' },
  neon: {
    ...BASE,
    factors: { display: 0.66, number: 0.85, lyric: 1.15, text: 0.82 },
    pill: 'tube',
    kicker: 'tube',
  },
  handmade: {
    ...BASE,
    factors: { display: 1.3, number: 1.1, lyric: 1, text: 1.2 },
    cover: {
      ...BASE.cover,
      preSave: 400,
      outNow: 540,
      announce: 400,
      marginTop: { preSave: 16, outNow: 16, announce: 16 },
    },
    feedCover: {
      ...BASE.feedCover,
      preSave: 370,
      outNow: 440,
      announce: 320,
      marginTop: { preSave: 14, outNow: 14, announce: 10 },
    },
    pillTextPx: 54,
  },
};

/** The frame of a theme id of the `theme` prop; an unknown id gets the Clean frame. */
export function storyFrameOf(themeId: string): StoryFrame {
  return isReleaseThemeId(themeId) ? STORY_FRAMES[themeId] : STORY_FRAMES.clean;
}

/** The theme id of the `theme` prop, as a release theme; an unknown id is Clean. */
export function storyThemeIdOf(themeId: string): ReleaseThemeId {
  return isReleaseThemeId(themeId) ? themeId : 'clean';
}

/** The cover sizes of a theme in a format. */
export function storyCoverSizes(frame: StoryFrame, formatId: StoryFormatId | null | undefined): StoryCoverSizes {
  return storyFormatOf(formatId).cover === 'feed' ? frame.feedCover : frame.cover;
}

/**
 * The Story frame of the design: 1080 x 1920. The app covers the top 250 px and the bottom 340 px;
 * every text, the cover and the logo stay in the safe column, y 270 to 1560.
 */
export const STORY_SIZE = STORY_FORMATS.story.size;
export const STORY_SAFE_TOP = STORY_FORMATS.story.safeTop;
export const STORY_SAFE_BOTTOM = STORY_FORMATS.story.safeBottom;
/** The safe band as shares of the frame height (`validateAssetPlan` rule 10). */
export const STORY_SAFE_TOP_SHARE = STORY_SAFE_TOP / STORY_SIZE.height;
export const STORY_SAFE_BOTTOM_SHARE = STORY_SAFE_BOTTOM / STORY_SIZE.height;
/** The kit logo: the first item of the safe column, centred, at most 96 px high. */
export const STORY_LOGO_MAX_HEIGHT = 96;
export const STORY_LOGO_MAX_WIDTH = 360;

/**
 * The logo box of a Story (or a feed post) as shares of the frame: centred at the top of the safe
 * column. The logo is contained in it (`objectFit: contain`), so a wide logo is at most 360 px wide
 * and a tall one 96 px high (in the canvas of the format).
 */
export function storyLogoBox(formatId: StoryFormatId = 'story'): { x: number; y: number; w: number; h: number } {
  const round = (value: number): number => Math.round(value * 10000) / 10000;
  const { size, safeTop } = STORY_FORMATS[formatId];
  return {
    x: round((size.width - STORY_LOGO_MAX_WIDTH) / 2 / size.width),
    y: round(safeTop / size.height),
    w: round(STORY_LOGO_MAX_WIDTH / size.width),
    h: round(STORY_LOGO_MAX_HEIGHT / size.height),
  };
}
